import { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  phaseDurationDays,
  phaseTotals,
  workbookTotals,
  type BidWorkbook,
} from './bidWorkbook';
import type { Bid } from '../bidStore';
import { money, moneyCompact, percent, useFinancialVisible } from '../financialDisplay';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const COLOR_EQUIP = '#667eea';
const COLOR_LABOUR = '#764ba2';
const COLOR_SUB = '#f6a609';
const COLOR_PROFIT = '#22c55e';


interface BidDashboardProps {
  bid: Bid;
  workbook: BidWorkbook;
  onOpenPhase: (id: string) => void;
}

const BidDashboard = ({ bid, workbook, onOpenPhase }: BidDashboardProps): JSX.Element => {
  useFinancialVisible();   // re-render when financial visibility resolves
  const totals = useMemo(() => workbookTotals(workbook), [workbook]);

  const phaseRows = useMemo(
    () =>
      workbook.phases
        .map((p) => ({ phase: p, t: phaseTotals(p, workbook.rowLengthM), duration: phaseDurationDays(p) }))
        .sort((a, b) => b.t.total - a.t.total),
    [workbook],
  );

  const inhouse = totals.equipment + totals.labour;
  const totalDays = phaseRows.reduce((sum, r) => sum + r.duration, 0);
  const subPct = totals.baseCost > 0 ? (totals.subcontract / totals.baseCost) * 100 : 0;
  const margin = totals.bidTotal > 0 ? (totals.profit / totals.bidTotal) * 100 : 0;
  const largest = phaseRows[0]?.phase.name ?? '—';

  // Derive emptiness from PHASES, not cost: every rate is stripped for a financially-blind caller,
  // so a fully-estimated bid would otherwise render "No estimate yet — add phases and line items"
  // to someone who can neither see the estimate nor add to it.
  const hasData = workbook.phases.some((p) => p.items.length > 0);

  // ----- chart data -----
  const categoryDoughnut = {
    labels: ['Equipment', 'Labour', 'Subcontract'],
    datasets: [
      {
        data: [totals.equipment, totals.labour, totals.subcontract],
        backgroundColor: [COLOR_EQUIP, COLOR_LABOUR, COLOR_SUB],
        borderWidth: 0,
        hoverOffset: 6,
      },
    ],
  };

  const sourceDoughnut = {
    labels: ['In-house', 'Subcontract'],
    datasets: [
      {
        data: [inhouse, totals.subcontract],
        backgroundColor: ['#4f46e5', COLOR_SUB],
        borderWidth: 0,
        hoverOffset: 6,
      },
    ],
  };

  const phaseLabels = phaseRows.map((r) => r.phase.name);
  const stackedBar = {
    labels: phaseLabels,
    datasets: [
      { label: 'Equipment', data: phaseRows.map((r) => r.t.equipment), backgroundColor: COLOR_EQUIP, borderRadius: 4, stack: 's' },
      { label: 'Labour', data: phaseRows.map((r) => r.t.labour), backgroundColor: COLOR_LABOUR, borderRadius: 4, stack: 's' },
      { label: 'Subcontract', data: phaseRows.map((r) => r.t.subcontract), backgroundColor: COLOR_SUB, borderRadius: 4, stack: 's' },
    ],
  };

  const costPerMBar = {
    labels: phaseLabels,
    datasets: [
      {
        label: 'Cost / metre',
        data: phaseRows.map((r) => r.t.costPerM),
        backgroundColor: '#667eea',
        borderRadius: 4,
      },
    ],
  };

  // How the Total Bid is built: cost categories + profit on top.
  const compositionBar = {
    labels: ['Total Bid'],
    datasets: [
      { label: 'Equipment', data: [totals.equipment], backgroundColor: COLOR_EQUIP, borderRadius: 4, stack: 'c' },
      { label: 'Labour', data: [totals.labour], backgroundColor: COLOR_LABOUR, borderRadius: 4, stack: 'c' },
      { label: 'Subcontract', data: [totals.subcontract], backgroundColor: COLOR_SUB, borderRadius: 4, stack: 'c' },
      { label: 'Profit', data: [totals.profit], backgroundColor: COLOR_PROFIT, borderRadius: 4, stack: 'c' },
    ],
  };

  const compositionOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' as const, labels: { boxWidth: 12, padding: 14, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const value = ctx.parsed.x as number;
            const pct = totals.bidTotal > 0 ? Math.round((value / totals.bidTotal) * 100) : 0;
            return `${ctx.dataset.label}: ${money(value)} (${pct}%)`;
          },
        },
      },
    },
    scales: {
      x: { stacked: true, ticks: { callback: (v: any) => moneyCompact(Number(v)) }, grid: { color: '#f1f5f9' } },
      y: { stacked: true, grid: { display: false } },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    plugins: {
      legend: { position: 'bottom' as const, labels: { boxWidth: 12, padding: 14, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const value = ctx.parsed as number;
            const arr = ctx.dataset.data as number[];
            const sum = arr.reduce((a, b) => a + b, 0) || 1;
            return `${ctx.label}: ${money(value)} (${Math.round((value / sum) * 100)}%)`;
          },
        },
      },
    },
  };

  const stackedOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    onClick: (_e: any, els: any[]) => {
      if (els.length) {
        const idx = els[0].index as number;
        const id = phaseRows[idx]?.phase.id;
        if (id) onOpenPhase(id);
      }
    },
    plugins: {
      legend: { position: 'bottom' as const, labels: { boxWidth: 12, padding: 14, font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${money(ctx.parsed.x)}` } },
    },
    scales: {
      x: { stacked: true, ticks: { callback: (v: any) => moneyCompact(Number(v)) }, grid: { color: '#f1f5f9' } },
      y: { stacked: true, ticks: { font: { size: 11 } }, grid: { display: false } },
    },
  };

  const costPerMOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx: any) => `${money(ctx.parsed.x)} / m` } },
    },
    scales: {
      x: { ticks: { callback: (v: any) => money(Number(v)) }, grid: { color: '#f1f5f9' } },
      y: { ticks: { font: { size: 11 } }, grid: { display: false } },
    },
  };

  if (!hasData) {
    return (
      <div className="bid-dashboard bid-dashboard-empty">
        <h2 className="bid-panel-title">No estimate yet</h2>
        <p className="bid-panel-subtitle">
          Add phases and line items in the <strong>Bid Details</strong> tab — the dashboard will fill in automatically.
        </p>
      </div>
    );
  }

  return (
    <div className="bid-dashboard">
      {/* KPI cards */}
      <div className="bid-kpi-grid">
        <div className="bid-kpi bid-kpi-accent">
          <span className="bid-kpi-label">Total Bid</span>
          <span className="bid-kpi-value">{moneyCompact(totals.bidTotal)}</span>
          <span className="bid-kpi-sub">{money(totals.costPerM)} / metre</span>
        </div>
        <div className="bid-kpi">
          <span className="bid-kpi-label">Base Cost</span>
          <span className="bid-kpi-value">{moneyCompact(totals.baseCost)}</span>
          <span className="bid-kpi-sub">before profit</span>
        </div>
        <div className="bid-kpi bid-kpi-profit">
          <span className="bid-kpi-label">Profit</span>
          <span className="bid-kpi-value">{moneyCompact(totals.profit)}</span>
          <span className="bid-kpi-sub">{percent(workbook.profitPct)} markup · {percent(margin)} margin</span>
        </div>
        <div className="bid-kpi">
          <span className="bid-kpi-label">Phases</span>
          <span className="bid-kpi-value">{workbook.phases.length}</span>
          <span className="bid-kpi-sub">{Math.round(totalDays)} phase-days</span>
        </div>
        <div className="bid-kpi">
          <span className="bid-kpi-label">Subcontracted</span>
          <span className="bid-kpi-value">{Math.round(subPct)}%</span>
          <span className="bid-kpi-sub">{moneyCompact(totals.subcontract)}</span>
        </div>
        <div className="bid-kpi">
          <span className="bid-kpi-label">Largest Phase</span>
          <span className="bid-kpi-value bid-kpi-value-sm">{largest}</span>
          <span className="bid-kpi-sub">{moneyCompact(phaseRows[0]?.t.total ?? 0)}</span>
        </div>
      </div>

      {/* Charts */}
      <div className="bid-charts-grid">
        <div className="bid-chart-card bid-chart-card-wide">
          <h3 className="bid-chart-title">
            Bid Composition <span className="bid-chart-hint">(cost + {moneyCompact(totals.profit)} profit = {moneyCompact(totals.bidTotal)})</span>
          </h3>
          <div className="bid-chart-box" style={{ height: 150 }}>
            <Bar data={compositionBar} options={compositionOptions} />
          </div>
        </div>
        <div className="bid-chart-card">
          <h3 className="bid-chart-title">Cost by Category</h3>
          <div className="bid-chart-box bid-chart-box-sm"><Doughnut data={categoryDoughnut} options={doughnutOptions} /></div>
        </div>
        <div className="bid-chart-card">
          <h3 className="bid-chart-title">In-house vs Subcontract</h3>
          <div className="bid-chart-box bid-chart-box-sm"><Doughnut data={sourceDoughnut} options={doughnutOptions} /></div>
        </div>
        <div className="bid-chart-card bid-chart-card-wide">
          <h3 className="bid-chart-title">Cost by Phase <span className="bid-chart-hint">(click a bar to open)</span></h3>
          <div className="bid-chart-box" style={{ height: Math.max(240, phaseRows.length * 34) }}>
            <Bar data={stackedBar} options={stackedOptions} />
          </div>
        </div>
        <div className="bid-chart-card bid-chart-card-wide">
          <h3 className="bid-chart-title">Cost per Metre by Phase</h3>
          <div className="bid-chart-box" style={{ height: Math.max(240, phaseRows.length * 34) }}>
            <Bar data={costPerMBar} options={costPerMOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BidDashboard;
